import React, { forwardRef } from 'react';
import { createMemoStyles, darken, lighten, defaultBaseline, defaultFonts, visuallyHidden, clsx, safeRest, KEYS } from '@wds/utils';
/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
/** Resolves the correct color */


var getMarkedColor = function (color, palette) {
  if (!color || typeof color !== 'string') {
    return palette.primary.main;
  }

  if (color.startsWith('data-')) {
    return palette.data[color.replace('data-', '')];
  }

  return palette[color].main;
};

var useStyles$1 = createMemoStyles(function (theme) {
  var palette = theme.palette,
      border = theme.border,
      spacing = theme.spacing,
      motion = theme.motion,
      type = theme.type;
  var list = {
    addonIconColor: palette.background.dark,
    affordanceIconColor: palette.secondary.main,
    itemHeight: 48,
    itemMinHeight: 48,
    itemDenseMinHeight: 40,
    labelFontSize: type.fontSize.bodySm,
    markWidth: 4
  };
  var listStyles = {
    wrenchListRow: {
      borderBottom: border.main,
      '&:last-child': {
        borderBottom: 'none'
      },
      'a&': {
        color: palette.text.dark,
        '&:hover, &:focus': {
          textDecoration: 'none'
        }
      }
    },
    wrenchListItem: function (_a) {
      var accordion = _a.accordion,
          color = _a.color,
          marked = _a.marked,
          menu = _a.menu,
          disabled = _a.disabled,
          upgrade = _a.upgrade,
          interactive = _a.interactive,
          isFocused = _a.isFocused,
          transparent = _a.transparent;
      return __assign(__assign(__assign(__assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), {
        fontWeight: menu ? type.fontWeight.regular : type.fontWeight.medium,
        display: 'flex',
        background: palette.background.light,
        position: 'relative',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: list.itemMinHeight,
        'a&': {
          textDecoration: 'none'
        }
      }), accordion && {
        '&:hover, &:focus, &:active': {
          cursor: 'pointer'
        }
      }), transparent ? {
        background: 'none'
      } : undefined), {
        // same specificity as parent List rules
        '[class^="wrenchList-"] &': __assign(__assign(__assign(__assign(__assign({}, interactive && __assign(__assign({
          transition: 'background 180ms linear'
        }, isFocused && {
          transition: 'background 125ms linear',
          background: palette.background.accent,
          cursor: 'pointer'
        }), {
          '&:hover, &:focus': {
            transition: 'background 125ms linear',
            background: palette.background.accent,
            cursor: 'pointer'
          },
          '&:active': {
            transition: 'background 125ms linear',
            background: darken(0.05, palette.background.accent)
          }
        })), upgrade && {
          background: lighten(0.42, palette.upgrade.main) // same specificity as transparent List rule

        }), upgrade && interactive && {
          '&:hover, &:focus': {
            background: lighten(0.35, palette.upgrade.main) // same specificity as transparent List rule

          }
        }), disabled && {
          opacity: 0.5,
          pointerEvents: 'none'
        }), {
          '&:before': __assign({}, (color || marked) && {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: list.markWidth,
            background: getMarkedColor(color, palette)
          })
        })
      });
    },
    wrenchListAddon: function () {
      return {
        padding: [0, spacing[4], 0, spacing[5]],
        color: list.addonIconColor,
        display: 'flex',
        alignItems: 'center',
        '> [class^="wrenchIcons-"]': {
          marginLeft: spacing[2]
        }
      };
    },

    /** Additional styles when addon is on the right */
    wrenchListAddonOnRight: function () {
      return {
        paddingLeft: 0,
        paddingRight: spacing[6],
        color: palette.text.lightMuted
      };
    },
    wrenchListAffordance: function (_a) {
      var addOnLeft = _a.addOnLeft;
      return __assign({
        padding: [0, spacing[6], 0, 0],
        color: list.affordanceIconColor,
        display: 'flex',
        alignItems: 'center',
        '& > *': {
          transition: "transform 150ms ".concat(motion.curve.ease)
        }
      }, addOnLeft && {
        paddingLeft: 0
      });
    },
    wrenchListLabel: function (_a) {
      var addOnLeft = _a.addOnLeft;
      return __assign({
        flexGrow: 1,
        padding: [0, spacing[5]],
        fontSize: list.labelFontSize,
        lineHeight: 1.25,
        minWidth: 0,
        'a &': {
          color: palette.text.dark
        }
      }, addOnLeft && {
        paddingLeft: 0
      });
    },
    wrenchPaneHide: function (_a) {
      var expanded = _a.expanded;
      return expanded ? null : visuallyHidden;
    }
  };
  return listStyles;
});
var ListItem = forwardRef(function (_a, ref) {
  var addOnLeft = _a.addOnLeft,
      addOnRight = _a.addOnRight,
      affordance = _a.affordance,
      children = _a.children,
      color = _a.color,
      component = _a.component,
      disabled = _a.disabled,
      id = _a.id,
      index = _a.index,
      isFocused = _a.isFocused,
      interactive = _a.interactive,
      marked = _a.marked,
      menu = _a.menu,
      navigationTarget = _a.navigationTarget,
      onClick = _a.onClick,
      upgrade = _a.upgrade,
      isNavigable = _a.isNavigable,
      transparent = _a.transparent,
      other = __rest(_a, ["addOnLeft", "addOnRight", "affordance", "children", "color", "component", "disabled", "id", "index", "isFocused", "interactive", "marked", "menu", "navigationTarget", "onClick", "upgrade", "isNavigable", "transparent"]); // no reason to expose these in props


  var accordion = other.accordion,
      expanded = other.expanded,
      paneComponent = other.paneComponent,
      rest = __rest(other, ["accordion", "expanded", "paneComponent"]);

  var _b = useStyles$1({
    accordion: accordion,
    color: color,
    marked: marked,
    menu: menu,
    disabled: disabled,
    upgrade: upgrade,
    isFocused: isFocused,
    interactive: interactive,
    addOnLeft: addOnLeft,
    expanded: expanded,
    transparent: transparent
  }),
      wrenchListRow = _b.wrenchListRow,
      wrenchListItem = _b.wrenchListItem,
      wrenchListLabel = _b.wrenchListLabel,
      wrenchListAddon = _b.wrenchListAddon,
      wrenchListAddonOnRight = _b.wrenchListAddonOnRight,
      wrenchListAffordance = _b.wrenchListAffordance;

  var addOnClassNames = clsx(wrenchListAddon, wrenchListAddonOnRight);
  var href = isNavigable ? navigationTarget : undefined;
  var ListItemWrapper = isNavigable ? component !== null && component !== void 0 ? component : 'a' : 'div';
  var interactiveProps = interactive ? {
    tabIndex: disabled ? '-1' : 0,
    role: 'button'
  } : {};

  var onHandleKeyDown = function (e) {
    if (onClick && (e.key === KEYS.ENTER || e.key === KEYS.SPACE)) {
      onClick(e);
    }
  };

  return React.createElement("li", {
    className: wrenchListRow
  }, React.createElement(ListItemWrapper, __assign({
    disabled: disabled,
    className: wrenchListItem,
    onClick: function (e) {
      return onClick === null || onClick === void 0 ? void 0 : onClick(e);
    },
    onKeyDown: onHandleKeyDown,
    href: href,
    id: id,
    "data-index": index,
    ref: ref
  }, interactiveProps, safeRest(rest)), addOnLeft && React.createElement("div", {
    className: wrenchListAddon
  }, addOnLeft), React.createElement("span", {
    className: wrenchListLabel
  }, children), addOnRight && React.createElement("div", {
    className: addOnClassNames
  }, addOnRight), affordance && React.createElement("span", {
    className: wrenchListAffordance
  }, affordance)), typeof expanded !== 'undefined' && paneComponent);
});
ListItem.defaultProps = {
  accordion: false,
  addOnLeft: undefined,
  addOnRight: undefined,
  affordance: undefined,
  children: undefined,
  color: undefined,
  component: undefined,
  disabled: false,
  id: undefined,
  index: undefined,
  interactive: false,
  isFocused: false,
  isNavigable: false,
  marked: false,
  menu: false,
  // eslint-disable-next-line no-script-url
  navigationTarget: 'javascript:void(0)',
  onClick: undefined,
  upgrade: false
}; // The displayName is needed for component's Accordion and perhaps other consumers in the future
// https://reactjs.org/docs/forwarding-refs.html#displaying-a-custom-name-in-devtools

ListItem.displayName = 'ListItem';
var useStyles = createMemoStyles(function (theme) {
  var spacing = theme.spacing,
      type = theme.type;
  var list = {
    itemDenseMinHeight: 40
  };
  var listStyles = {
    wrenchList: function (_a) {
      var dense = _a.dense,
          noRules = _a.noRules;
      return __assign(__assign(__assign(__assign({}, defaultBaseline), {
        display: 'block'
      }), dense && {
        '& [class^="wrenchListAddon-"]': {
          paddingLeft: spacing[4],
          '& [class^="wrenchIcons-"]': {
            paddingLeft: 0
          }
        },
        // second className so it needs `*- not ^=` selector
        '& [class*="wrenchListAddonOnRight-"]': {
          paddingRight: spacing[4]
        },
        '& [class^="wrenchListLabel-"]': {
          fontWeight: type.fontWeight.regular,
          margin: [spacing[3] + 1, 0]
        },
        '& [class^="wrenchListItem-"]': {
          minHeight: list.itemDenseMinHeight,
          borderBottom: 'none'
        }
      }), noRules && {
        '& [class^="wrenchListRow-"]': {
          borderBottom: 'none'
        }
      });
    }
  };
  return listStyles;
});
var List = forwardRef(function (_a, ref) {
  var dense = _a.dense,
      interactive = _a.interactive,
      noRules = _a.noRules,
      transparent = _a.transparent,
      children = _a.children,
      role = _a.role,
      id = _a.id;
  var wrenchList = useStyles({
    dense: dense,
    interactive: interactive,
    noRules: noRules,
    transparent: transparent
  }).wrenchList;
  var newChildren = React.Children.map(children, function (child, index) {
    // Case wherte we have to allow transparent only for clone element?
    return React.isValidElement(child) && React.cloneElement(child, {
      interactive: interactive,
      index: index,
      transparent: transparent
    });
  });
  return React.createElement("ul", {
    ref: ref,
    className: wrenchList,
    role: role,
    id: id
  }, newChildren);
});
List.defaultProps = {
  dense: false,
  id: undefined,
  interactive: false,
  noRules: false,
  role: undefined,
  transparent: false
};
List.displayName = 'List';
List.Item = ListItem;
export { List };